import * as React from 'react'
import { KEYS } from 'platejs'
import { useEditorRef, useEditorSelector } from 'platejs/react'

import {
  BoldIcon,
  ItalicIcon,
  StrikethroughIcon,
  UnderlineIcon,
} from '../../../icons'
import { RichTextField } from '../../RichTextField'

type MarkToolbarButtonProps = Omit<
  React.ComponentPropsWithoutRef<typeof RichTextField.ToolbarButton>,
  'children' | 'label'
>

type MarkToolbarButtonInternalProps = MarkToolbarButtonProps & {
  icon: React.ReactElement
  label: string
  nodeType: string
}

const MarkToolbarButton = React.forwardRef<
  HTMLButtonElement,
  MarkToolbarButtonInternalProps
>(({ icon, label, nodeType, onClick, ...props }, ref) => {
  const editor = useEditorRef()
  const pressed = useEditorSelector(
    (currentEditor) => currentEditor.api.hasMark(nodeType),
    [nodeType],
  )

  return (
    <RichTextField.ToolbarButton
      {...props}
      ref={ref}
      aria-pressed={pressed}
      label={label}
      onClick={(event) => {
        onClick?.(event)
        if (!event.defaultPrevented) editor.tf.toggleMark(nodeType)
      }}
    >
      {icon}
    </RichTextField.ToolbarButton>
  )
})

MarkToolbarButton.displayName = 'RichTextField.MarkToolbarButton'

const BoldToolbarButton = React.forwardRef<
  HTMLButtonElement,
  MarkToolbarButtonProps
>((props, ref) => (
  <MarkToolbarButton
    {...props}
    ref={ref}
    icon={<BoldIcon />}
    label="Bold"
    nodeType={KEYS.bold}
  />
))

BoldToolbarButton.displayName = 'RichTextField.BoldToolbarButton'

const ItalicToolbarButton = React.forwardRef<
  HTMLButtonElement,
  MarkToolbarButtonProps
>((props, ref) => (
  <MarkToolbarButton
    {...props}
    ref={ref}
    icon={<ItalicIcon />}
    label="Italic"
    nodeType={KEYS.italic}
  />
))

ItalicToolbarButton.displayName = 'RichTextField.ItalicToolbarButton'

const UnderlineToolbarButton = React.forwardRef<
  HTMLButtonElement,
  MarkToolbarButtonProps
>((props, ref) => (
  <MarkToolbarButton
    {...props}
    ref={ref}
    icon={<UnderlineIcon />}
    label="Underline"
    nodeType={KEYS.underline}
  />
))

UnderlineToolbarButton.displayName = 'RichTextField.UnderlineToolbarButton'

const StrikethroughToolbarButton = React.forwardRef<
  HTMLButtonElement,
  MarkToolbarButtonProps
>((props, ref) => (
  <MarkToolbarButton
    {...props}
    ref={ref}
    icon={<StrikethroughIcon />}
    label="Strikethrough"
    nodeType={KEYS.strikethrough}
  />
))

StrikethroughToolbarButton.displayName =
  'RichTextField.StrikethroughToolbarButton'

export {
  BoldToolbarButton,
  ItalicToolbarButton,
  StrikethroughToolbarButton,
  UnderlineToolbarButton,
}
export type { MarkToolbarButtonProps }
